import { indianStatesAndUTs } from './dropdownOptions';

/**
 * The diet / nutrition questionnaire, described once.
 *
 * The public form at /public/diet/:patientId renders these fields, and the Diet
 * tab on the patient profile reads the saved answers back using the same labels,
 * so a question can never be worded one way to the patient and another way to the
 * doctor. Field `name`s match the keys that were already being posted, so forms
 * submitted before this existed still display correctly.
 */
export const DIET_SECTIONS = [
  {
    id: 'basic',
    title: 'Basic Information',
    description: 'A few details about you so we can size the plan correctly.',
    fields: [
      { name: 'fullName', label: 'Full Name', type: 'text', placeholder: 'Enter full name' },
      { name: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
      { name: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] },
      { name: 'bloodGroup', label: 'Blood Group', type: 'select', options: ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'] },
      {
        name: 'profession',
        label: 'Profession',
        type: 'select',
        options: ['Homemaker', 'Teacher', 'Student', 'Retired', 'IT', 'Business', 'Engineering', 'Medical', 'Grade I & II', 'Senior Management', 'Defence', 'Baby', 'Police', 'Unknown', 'Self Employed'],
      },
      { name: 'weight', label: 'Weight', type: 'number', unit: 'kg', placeholder: 'e.g. 68' },
      { name: 'height', label: 'Height', type: 'number', unit: 'cm', placeholder: 'e.g. 170' },
      { name: 'state', label: 'State', type: 'select', options: indianStatesAndUTs },
    ],
  },
  {
    id: 'fitness',
    title: 'Fitness Assessment',
    description: 'How active you are on a normal day.',
    fields: [
      { name: 'transport', label: 'How do you travel to work daily?', type: 'select', options: ['Public transport', 'Own vehicle', 'Walk', 'Cycle', 'Work from home'] },
      { name: 'workout', label: 'Do you work out?', type: 'select', options: ['Yes', 'No'] },
      { name: 'howlongworkout', label: 'What is your workout schedule, intensity and duration?', type: 'textarea', full: true },
      {
        name: 'exercises',
        label: 'What kind of exercise do you do?',
        type: 'select',
        options: ['Functional training', 'Hard core weight training', 'Power yoga', 'Cardio', 'Pilates', 'Kick boxing', 'Taichi (Japanese or Chinese workout)', 'None'],
      },
      { name: 'goal', label: 'What are your fitness goals?', type: 'textarea', full: true },
      {
        name: 'Measurements',
        label: 'Body measurements (cm)',
        type: 'textarea',
        full: true,
        placeholder: 'Chest, waist at navel, hips at the widest point, thigh, upper arm',
      },
      { name: 'weighteasily', label: 'Do you gain weight more easily on your hips or abdomen?', type: 'text' },
      { name: 'area', label: 'Do you store fat in any specific area?', type: 'text' },
      { name: 'gaingweight', label: 'At what age or stage of life did you start gaining weight, and why?', type: 'textarea', full: true },
    ],
  },
  {
    id: 'dietary',
    title: 'Dietary Assessment',
    description: 'What a normal week of eating looks like for you.',
    fields: [
      { name: 'veg', label: 'Vegetarian or Non-Vegetarian?', type: 'select', options: ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan', 'Jain'] },
      { name: 'drink', label: 'Do you smoke or drink?', type: 'select', options: ['Yes', 'No', 'Occasionally'] },
      { name: 'food', label: 'How often do you eat outside food?', type: 'text' },
      { name: 'fasting', label: 'Do you fast, and how often?', type: 'text' },
      { name: 'family', label: 'Number of people in the family', type: 'number' },
      { name: 'month', label: 'Edible oil used per month by the whole family', type: 'text', placeholder: 'e.g. 3 litres' },
      {
        name: 'edibleOil',
        label: 'Which edible oil do you use?',
        type: 'select',
        options: ['Olive Oil', 'Vegetable Oil', 'Coconut Oil', 'Canola Oil', 'Sunflower Oil', 'Peanut Oil', 'Sesame Oil', 'Groundnut Oil', 'Mustard Oil', 'Rice Bran Oil', 'Grapeseed Oil', 'Other'],
      },
      { name: 'gheeFrequency', label: 'How often is ghee part of your diet?', type: 'select', options: ['Daily', 'Weekly', 'Monthly', 'Occasionally', 'Rarely', 'Never'] },
      { name: 'waterIntake', label: 'Water intake per day', type: 'number', unit: 'glasses (200 ml)' },
      { name: 'constipationFrequency', label: 'How often do you feel constipated?', type: 'text' },
      { name: 'likedFood', label: 'Which foods do you like or eat most often?', type: 'textarea', full: true },
      { name: 'dislikedFood', label: 'Which foods do you not like or never eat?', type: 'textarea', full: true },
    ],
  },
  {
    id: 'recall',
    title: 'A Day in Your Life',
    description: 'Walk us through a typical day, from waking up to going to bed.',
    fields: [
      { name: 'wakeUpTime', label: 'What time do you wake up?', type: 'time' },
      { name: 'wakeUpMethod', label: 'Do you wake up with an alarm or on your own?', type: 'select', options: ['Alarm', 'On my own'] },
      { name: 'freshness', label: 'Do you wake up fresh, or feel like sleeping more?', type: 'select', options: ['Wake up fresh', 'Feel like sleeping more'] },
      { name: 'snore', label: 'Do you snore or make a whistling sound while sleeping?', type: 'select', options: ['Yes', 'No', "Don't know"] },
      { name: 'breakfastTime', label: 'What time do you have breakfast?', type: 'time' },
      { name: 'breakfastContent', label: 'What does your breakfast include?', type: 'textarea', full: true },
      { name: 'lunchTime', label: 'What time do you have lunch?', type: 'time' },
      { name: 'lunchContent', label: 'What does your lunch include?', type: 'textarea', full: true },
      { name: 'dinnerTime', label: 'What time do you have dinner?', type: 'time' },
      { name: 'dinnerContent', label: 'What does your dinner include?', type: 'textarea', full: true },
      { name: 'betweenMeals', label: 'Do you eat anything between meals?', type: 'textarea', full: true },
      { name: 'cravings', label: 'Do you get cravings?', type: 'select', options: ['Yes', 'No'] },
      { name: 'cravingTime', label: 'What time of day do you get cravings?', type: 'text' },
      { name: 'teaCoffeeConsumption', label: 'Tea / coffee — how many cups a day, with or without sugar?', type: 'text' },
      { name: 'hungerFeeling', label: 'Do you eat when hungry, by the clock, or both?', type: 'select', options: ['When hungry', 'By timings and schedule', 'Both'] },
      { name: 'mouthDryness', label: 'Do you feel parched or dry in the mouth?', type: 'select', options: ['Yes', 'No'] },
      { name: 'bedtimeHunger', label: 'Do you feel hungry before going to bed?', type: 'select', options: ['Yes', 'No'] },
      { name: 'sleepTime', label: 'What time do you go to sleep?', type: 'time' },
      { name: 'sleepRating', label: 'Rate your sleep', type: 'number', unit: 'out of 10', min: 0, max: 10 },
      { name: 'relationshipWithFood', label: 'Would you call your relationship with food good, bad or neutral?', type: 'select', options: ['Good', 'Bad', 'Neutral'] },
      { name: 'sexLife', label: 'How is your sex life, and how often in a week?', type: 'text', sensitive: true },
    ],
  },
];

/** Every field, flattened — handy for lookups. */
export const DIET_FIELDS = DIET_SECTIONS.flatMap((section) => section.fields);

/** A blank answer for every question, used to seed the public form. */
export const emptyDietForm = () =>
  DIET_FIELDS.reduce((acc, field) => {
    acc[field.name] = '';
    return acc;
  }, {});

/** Keys that are bookkeeping rather than answers, so views can skip them. */
export const DIET_META_KEYS = ['patientId', '_id', 'submittedAt'];
